import { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import { FaExclamationTriangle, FaPlus } from "react-icons/fa";
import useCommon from "hooks/useCommon";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import VuiInput from "components/VuiInput";

import IntegrationCard from "./components/IntegrationCard";
import CreateIntegrationDialog from "./components/CreateIntegration";

import Snackbar from "components/Snackbar";

import {
  createIntegration,
  resetCreateIntegrationState,
  getIntegrations,
} from "store/slice/integrationsSlice";
import { CircularProgress, Dialog, DialogContent } from "@mui/material";
import DeleteIntegrationDialog from "./components/DeleteIntegrationDialog";
import { deleteIntegration } from "store/slice/integrationsSlice";
import EditIntegrationDialog from "./components/EditIntegrationDialog";
import { updateIntegration } from "store/slice/integrationsSlice";
import { resetUpdateIntegrationState } from "store/slice/integrationsSlice";
import NoResults from "components/NoResult";

const Integrations = () => {
  const [search, setSearch] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [integrationToDelete, setIntegrationToDelete] = useState(null);
  const [toggleDialogOpen, setToggleDialogOpen] = useState(false);
  const [integrationToToggle, setIntegrationToToggle] = useState(null);

  const { STORE, DISPATCH } = useCommon();

  const { integrations, fetchLoading, fetchError, updateLoading } = STORE(
    (state) => state.integrations
  );

  const [snackbar, setSnackbar] = useState({
    show: false,
    color: "success",
    title: "",
    content: "",
    icon: "check",
  });

  const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, show: false }));

  // const integrations = [
  //   {
  //     _id: "prov_mews",
  //     key: "mews",
  //     displayName: "MEWS",
  //     description: "Property Management System",
  //     category: "PMS",
  //     logoUrl:
  //       "https://www.mews.com/hubfs/_Project_Phoenix/images/logo/Mews%20Logo%20-%20White.svg",
  //     status: "enabled",
  //     capabilities: {
  //       supportsWebhooks: true,
  //       supportsPolling: true,
  //       supportsPropertyDiscovery: true,
  //     },
  //   },
  //   {
  //     _id: "prov_slack",
  //     key: "slack",
  //     displayName: "Slack",
  //     description: "Internal notifications and alerts",
  //     category: "Communication",
  //     logoUrl: "https://cdn.worldvectorlogo.com/logos/slack-new-logo.svg",
  //     status: "enabled",
  //     capabilities: {
  //       supportsWebhooks: true,
  //       supportsPolling: false,
  //       supportsPropertyDiscovery: false,
  //     },
  //   },
  //   {
  //     _id: "prov_stripe",
  //     key: "stripe",
  //     displayName: "Stripe",
  //     description: "Payments and payouts",
  //     category: "Payments",
  //     logoUrl: "https://cdn.worldvectorlogo.com/logos/stripe-4.svg",
  //     status: "disabled",
  //     capabilities: {
  //       supportsWebhooks: true,
  //       supportsPolling: false,
  //       supportsPropertyDiscovery: false,
  //     },
  //   },
  // ];

  const handleAdd = () => {
    setSelectedIntegration(null);
    setOpenDialog(true);
  };

  const handleEdit = (integration) => {
    setSelectedIntegration(integration);
    setEditDialogOpen(true);
  };

  const handleDelete = (integration) => {
    setIntegrationToDelete(integration);
    setDeleteDialogOpen(true);
  };

  const handleCreateIntegrationProvider = async (data) => {
    await DISPATCH(createIntegration(data))
      .unwrap()
      .then((res) => {
        const success = res?.success ?? true;

        if (success) {
          setOpenDialog(false);
          setSnackbar({
            show: true,
            color: "success",
            title: "Integration Added",
            content: "Integration created successfully",
            icon: "check",
          });

          DISPATCH(resetCreateIntegrationState());
        }
      })
      .catch((err) => {
        setSnackbar({
          show: true,
          color: "error",
          title: "Error",
          content: err?.message || "Failed to create integration",
          icon: "warning",
        });
      });
  };

  const handleUpdateIntegration = async (values) => {
    await DISPATCH(
      updateIntegration({
        id: selectedIntegration._id,
        data: values,
      })
    )
      .unwrap()
      .then((res) => {
        const success = res?.success ?? true;

        if (success) {
          setEditDialogOpen(false);
          setSelectedIntegration(null);

          setSnackbar({
            show: true,
            color: "success",
            title: "Integration Updated",
            content: "Integration updated successfully",
            icon: "check",
          });

          DISPATCH(resetUpdateIntegrationState?.());
        }
      })
      .catch((err) => {
        setSnackbar({
          show: true,
          color: "error",
          title: "Error",
          content: err?.message || "Failed to update integration",
          icon: "warning",
        });
      });
  };

  const handleConfirmDelete = async () => {
    if (!integrationToDelete) return;

    await DISPATCH(deleteIntegration(integrationToDelete))
      .unwrap()
      .then(() => {
        setSnackbar({
          show: true,
          color: "success",
          title: "Integration Deleted",
          content: "Integration removed successfully",
          icon: "check",
        });

        setDeleteDialogOpen(false);
        setIntegrationToDelete(null);
      })
      .catch((err) => {
        setSnackbar({
          show: true,
          color: "error",
          title: "Delete Failed",
          content: err?.message || "Failed to delete integration",
          icon: "warning",
        });
      });
  };

  const handleToggle = (integration) => {
    setIntegrationToToggle(integration);
    setToggleDialogOpen(true);
  };

  const handleConfirmToggle = async () => {
    if (!integrationToToggle) return;

    const nextStatus = integrationToToggle.status === "enabled" ? "disabled" : "enabled";

    await DISPATCH(
      updateIntegration({
        id: integrationToToggle._id,
        data: { status: nextStatus },
      })
    )
      .unwrap()
      .then(() => {
        setSnackbar({
          show: true,
          color: "success",
          title: "Status Updated",
          content: `Integration ${nextStatus === "enabled" ? "enabled" : "disabled"} successfully`,
          icon: "check",
        });

        setToggleDialogOpen(false);
        setIntegrationToToggle(null);
      })
      .catch((err) => {
        setSnackbar({
          show: true,
          color: "error",
          title: "Update Failed",
          content: err?.message || "Failed to update integration status",
          icon: "warning",
        });
      });
  };

  useEffect(() => {
    // if (integrations.length > 0) return;
    DISPATCH(getIntegrations());
  }, [DISPATCH]);

  const filteredIntegrations = integrations.filter((i) =>
    i.displayName.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <VuiBox py={3}>
        <VuiBox mb={3}>
          <Card>
            {/* Header */}
            <VuiBox
              display="flex"
              justifyContent="space-between"
              mb="22px"
              flexDirection={{ xs: "column", md: "row" }}
              alignItems={{ xs: "stretch", md: "center" }}
              gap={2}
            >
              <VuiTypography
                variant="lg"
                sx={{
                  width: { xs: "100%", md: "auto" },
                  textAlign: { xs: "left", md: "left" },
                }}
              >
                Manage Integrations
              </VuiTypography>

              <VuiBox display="flex" alignItems="center" gap={2}>
                <VuiBox width="220px">
                  <VuiInput
                    gradient
                    fullWidth
                    placeholder="Search integrations..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </VuiBox>

                <VuiButton
                  variant="contained"
                  color="primary"
                  onClick={handleAdd}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    width: { md: "40%", xs: "50%" },
                  }}
                >
                  <FaPlus size={12} /> Add
                  <VuiTypography
                    variant="text"
                    color="white"
                    sx={{ display: { xs: "none", sm: "block" }, lineHeight: 1 }}
                  >
                    Integration
                  </VuiTypography>
                </VuiButton>
              </VuiBox>
            </VuiBox>
            {!fetchLoading && fetchError && (
              <VuiBox
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                height="200px"
                gap={1}
              >
                <VuiBox display="flex" alignItems="center" justifyContent="center" gap={1}>
                  <FaExclamationTriangle size={18} color="#f44336" />

                  <VuiTypography variant="body2" sx={{ lineHeight: 1 }}>
                    Failed to load integrations
                  </VuiTypography>
                </VuiBox>

                <VuiTypography variant="caption" opacity={0.7}>
                  {fetchError?.message}
                </VuiTypography>

                <VuiButton
                  size="small"
                  variant="outlined"
                  color="primary"
                  onClick={() => DISPATCH(getIntegrations())}
                  sx={{ mt: 1 }}
                >
                  Retry
                </VuiButton>
              </VuiBox>
            )}
            {/* Cards Grid */}
            <VuiBox px={2} pb={4} mt={4}>
              <Grid container spacing={3}>
                {fetchLoading ? (
                  <VuiBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    width="100%"
                    minHeight="200px"
                  >
                    <CircularProgress size={40} color="primary" />
                  </VuiBox>
                ) : filteredIntegrations.length === 0 ? (
                  <Grid item xs={12}>
                    <NoResults title="No integrations found" search={search} />
                  </Grid>
                ) : (
                  filteredIntegrations.map((integration) => (
                    <Grid item xs={12} md={6} lg={4} key={integration._id}>
                      <IntegrationCard
                        integration={integration}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onToggle={handleToggle}
                      />
                    </Grid>
                  ))
                )}
              </Grid>
            </VuiBox>
          </Card>
        </VuiBox>
      </VuiBox>

      <CreateIntegrationDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onSubmit={handleCreateIntegrationProvider}
      />

      <EditIntegrationDialog
        open={editDialogOpen}
        integration={selectedIntegration}
        onSubmit={handleUpdateIntegration}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedIntegration(null);
        }}
      />

      <DeleteIntegrationDialog
        open={deleteDialogOpen}
        integration={integrationToDelete}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
      />
      <Dialog
        open={toggleDialogOpen}
        onClose={() => {
          setToggleDialogOpen(false);
          setIntegrationToToggle(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent>
          <VuiBox
            display="flex"
            flexDirection="column"
            alignItems="center"
            textAlign="center"
            px={2}
            py={3}
          >
            <VuiBox mb={2}>
              <FaExclamationTriangle size={36} color="#ff4d4f" />
            </VuiBox>

            <VuiTypography
              variant="body2"
              opacity={0.85}
              sx={{ lineHeight: 1.6, letterSpacing: 0.35 }}
            >
              Are you sure you want to{" "}
              <strong>{integrationToToggle?.status === "enabled" ? "disable" : "enable"}</strong>{" "}
              the <strong>{integrationToToggle?.displayName}</strong> integration?
            </VuiTypography>

            <VuiBox display="flex" justifyContent="center" gap={1.5} mt={3} width="100%">
              <VuiButton
                variant="outlined"
                color="primary"
                onClick={() => {
                  setToggleDialogOpen(false);
                  setIntegrationToToggle(null);
                }}
              >
                Cancel
              </VuiButton>

              <VuiButton
                variant="contained"
                color="success"
                disabled={updateLoading}
                onClick={handleConfirmToggle}
              >
                {updateLoading ? "Updating..." : "Confirm"}
              </VuiButton>
            </VuiBox>
          </VuiBox>
        </DialogContent>
      </Dialog>

      <Snackbar
        color={snackbar.color}
        icon={snackbar.icon}
        title={snackbar.title}
        content={snackbar.content}
        open={snackbar.show}
        onClose={closeSnackbar}
        close={closeSnackbar}
      />
    </DashboardLayout>
  );
};

export default Integrations;
